import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import {
  getSOVLines,
  createSOVLine,
  bulkCreateSOVLines,
  updateSOVLine,
  deleteSOVLine,
  checkSOVAllocation,
  rollupSOVLine,
  resetSOVProgress,
  getSOVChangeLog,
  getSections,
  createSection,
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import LoadingState from './LoadingState';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';
import { Icon, SegmentedControl, StateScreen, LoadingDialog } from './ui';

/* -------------------- SOV toolbar helpers --------------------
 * Ghost + icon-only buttons consistent with the Schedule action bar.
 * Module-scope so they don't re-render on every SOVTab tick.
 */
function SovGhostBtn({ icon, label, onClick, disabled }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        height: 28, padding: '0 10px',
        border: '1px solid transparent',
        background: hover && !disabled ? 'var(--bg-hover)' : 'transparent',
        color: disabled ? 'var(--text-4)' : 'var(--text-3)',
        fontSize: 13.5, fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: 6,
        whiteSpace: 'nowrap',
        transition: 'background .12s, color .12s',
      }}
    >
      {icon && <Icon name={icon} size={13} />}
      {label}
    </button>
  );
}
function SovIconBtn({ name, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" title={label} aria-label={label} onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28, height: 28, border: 'none', borderRadius: 6,
        background: hover ? 'var(--bg-hover)' : 'transparent',
        color: hover ? 'var(--text-1)' : 'var(--text-3)',
        cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', padding: 0,
        transition: 'background .12s, color .12s',
      }}
    >
      <Icon name={name} size={14} />
    </button>
  );
}
/* -------------------- Sort + filter wiring --------------------
 *
 * SORT_OPTIONS is the catalog used by both the Sort popover (label +
 * key) and the comparator the table uses at render time. `key` is
 * the value stored in component state. Each `cmp(a, b, ctx)` returns
 * a negative/zero/positive number per Array.sort convention, with
 * `ctx.sectionLookup` available to look up section_name from
 * section_id. Tiebreakers fall through to section then code so the
 * table always groups visually under the AIA G703 conventions.
 */
const SOV_NUM_FROM_CODE = (c) => (c ? parseInt(String(c).replace(/\D/g, ''), 10) || 0 : 0);
const SOV_SECTION_NAME = (line, ctx) =>
  (ctx.sectionLookup[line.section_id]?.section_name || '￿');

const SORT_OPTIONS = [
  {
    key: 'section', label: 'Section + Code (default)',
    cmp: (a, b, ctx) => {
      const aSec = SOV_SECTION_NAME(a, ctx);
      const bSec = SOV_SECTION_NAME(b, ctx);
      if (aSec !== bSec) return aSec.localeCompare(bSec);
      return SOV_NUM_FROM_CODE(a.sov_code) - SOV_NUM_FROM_CODE(b.sov_code);
    },
  },
  {
    key: 'code',    label: 'Code (A→Z)',
    cmp: (a, b) => (a.sov_code || '').localeCompare(b.sov_code || ''),
  },
  {
    key: 'desc',    label: 'Description (A→Z)',
    cmp: (a, b) => (a.description || '').localeCompare(b.description || ''),
  },
  {
    key: 'sched_desc', label: 'Contract amount (high → low)',
    cmp: (a, b) => (parseFloat(b.contract_amount) || 0) - (parseFloat(a.contract_amount) || 0),
  },
  {
    key: 'sched_asc',  label: 'Contract amount (low → high)',
    cmp: (a, b) => (parseFloat(a.contract_amount) || 0) - (parseFloat(b.contract_amount) || 0),
  },
  {
    key: 'pct_asc', label: '% Billed (low → high)',
    cmp: (a, b, ctx) => ctx.pctOf(a) - ctx.pctOf(b),
  },
  {
    key: 'pct_desc', label: '% Billed (high → low)',
    cmp: (a, b, ctx) => ctx.pctOf(b) - ctx.pctOf(a),
  },
  {
    key: 'balance_desc', label: 'Balance to finish (high → low)',
    cmp: (a, b, ctx) => ctx.balanceOf(b) - ctx.balanceOf(a),
  },
  {
    key: 'balance_asc', label: 'Balance to finish (low → high)',
    cmp: (a, b, ctx) => ctx.balanceOf(a) - ctx.balanceOf(b),
  },
  {
    key: 'retainage_desc', label: 'Retainage held (high → low)',
    cmp: (a, b) => (parseFloat(b.retainage_amount) || 0) - (parseFloat(a.retainage_amount) || 0),
  },
  {
    key: 'released_desc', label: 'Retainage released (high → low)',
    cmp: (a, b) => (parseFloat(b.retainage_released) || 0) - (parseFloat(a.retainage_released) || 0),
  },
];

/** Lightweight popover anchored to a trigger via getBoundingClientRect.
 *  Used for both the Filter and Sort menus. Closes on outside-click,
 *  Escape, or page-scroll. `keepOpenOnClick` lets the Filter menu's
 *  checkbox items toggle without dismissing.
 */
function SovPopover({ open, anchor, onClose, children, minWidth = 220 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onScroll = () => onClose();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, onClose]);
  if (!open || !anchor) return null;
  const top = anchor.bottom + 4;
  const left = Math.max(8, anchor.left);
  return createPortal(
    <div
      ref={ref}
      role="menu"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', top, left, minWidth, zIndex: 1000,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 8,
        boxShadow: 'var(--shadow-md)',
        padding: 4,
        fontSize: 14.5,
        maxHeight: '60vh', overflowY: 'auto',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** Row used inside the Filter popover. 16px checkbox + label.
 *  Mousedown is stopped so the parent SovPopover's outside-click
 *  listener (mousedown-based) doesn't fire from inside and close
 *  the popover during a multi-select toggle. */
function FilterCheckRow({ checked, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '7px 10px',
        border: 'none',
        background: hover ? 'var(--bg-hover)' : 'transparent',
        color: 'var(--text-1)', cursor: 'pointer',
        borderRadius: 5,
        textAlign: 'left', fontSize: 14, fontWeight: 500,
        transition: 'background .1s',
      }}
    >
      <span style={{
        width: 14, height: 14, borderRadius: 3,
        background: checked ? 'var(--accent)' : 'var(--bg-surface)',
        border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-default)'}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', flexShrink: 0,
        transition: 'background .12s, border-color .12s',
      }}>
        {checked && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

/** Stable hash → OKLCH hue for a section name. Mirror of Schedule
 *  helpers so SOV section dots match Schedule section dots when the
 *  same section name appears in both. */
function sovSectionHue(name) {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}
const sovSectionColor = (name) => `oklch(0.55 0.1 ${sovSectionHue(name)})`;

// Category color mapping matching Base44 design
const getCategoryStyle = (category) => {
  switch ((category || '').toLowerCase()) {
    case 'material':
      return { backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' };
    case 'labor':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'general conditions':
      return { backgroundColor: '#F5F5F5', color: '#616161', border: '1px solid #BDBDBD' };
    case 'equipment':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    case 'other':
      return { backgroundColor: '#F3E5F5', color: '#6A1B9A', border: '1px solid #BA68C8' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#616161', border: '1px solid #BDBDBD' };
  }
};

const SOVTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [sovLines, setSovLines] = useState([]);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // Double-submit guards layered on top of the existing page-level
  // `loading`. `creatingLine` covers the manual-add modal Submit button,
  // `deletingId` is per-row so an in-flight delete only freezes its own
  // trash icon.
  const [creatingLine, setCreatingLine] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    sov_code: '',
    description: '',
    building_area_phase: '',
    billing_category: 'Labor',
    contract_amount: '',
    billing_method: 'Percent Complete',
    retainage_percent: 0,
    notes: '',
  });
  // Default to true so the very first render shows the loading spinner
  // instead of flashing the "No SOV lines yet" empty state before the
  // initial fetchSOVLines() resolves. Flips to false in fetchSOVLines's
  // finally block as usual.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Progress dialog for the multi-row file import. Shape:
  //   null                                            -> dialog hidden
  //   { current, total, status: 'importing' }         -> in progress
  //   { current, total, status: 'success' }           -> done, auto-closes
  //   { current, total, status: 'error', error: str } -> failed, user dismisses
  const [importProgress, setImportProgress] = useState(null);

  // SOV Properties modal
  const [propsModal, setPropsModal] = useState(null); // SOV line object or null
  const [propsForm, setPropsForm] = useState({});
  const [propsChangeLog, setPropsChangeLog] = useState([]);
  const [propsSaving, setPropsSaving] = useState(false);
  const [propsChangeReason, setPropsChangeReason] = useState('');

  // Per-project sections (drive the section dropdown in the
  // properties modal + carry through to the CREATE_SOV payload for
  // CE-Agent grouping). Fetched on projectId change; the inline
  // "+ Add new section..." flow lets the PM create a section
  // without leaving the SOV line they're editing.
  const [sections, setSections] = useState([]);
  const fetchSections = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getSections(projectId);
      setSections(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Failed to fetch sections:', e);
    }
  }, [projectId]);
  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const openPropsModal = async (line) => {
    setPropsModal(line);
    setPropsForm({
      sov_code: line.sov_code || '',
      description: line.description || '',
      building_area_phase: line.building_area_phase || '',
      billing_category: line.billing_category || 'Labor',
      contract_amount: line.contract_amount || 0,
      retainage_percent: line.retainage_percent ?? 5,
      retainage_percent_stored: line.retainage_percent_stored ?? line.retainage_percent ?? 5,
      billing_method: line.billing_method || 'Percent Complete',
      notes: line.notes || '',
      // section_id may be null (unsectioned) -- the dropdown handles
      // null by showing "— Unsectioned —" as the selected option.
      section_id: line.section_id ?? null,
    });
    setPropsChangeReason('');
    try {
      const logs = await getSOVChangeLog(line.id);
      setPropsChangeLog(logs);
    } catch { setPropsChangeLog([]); }
  };

  const handlePropsSave = async () => {
    if (!propsModal) return;
    setPropsSaving(true);
    try {
      await updateSOVLine(propsModal.id, { ...propsForm, change_reason: propsChangeReason || null });
      setPropsModal(null);
      await fetchSOVLines();
      if (refreshSummary) refreshSummary();
    } catch (e) {
      alert(e.message || 'Failed to save');
    } finally { setPropsSaving(false); }
  };

  // Billed vs Projected toggle
  // Default to Billed (last-finalized values) rather than Projected.
  // Billed is the source of truth that matches what CE Agent received
  // and what shows on AFP statements; Projected is the live task-rolled
  // forecast which is more of a "what if" view. Opening the SOV tab
  // should show the bill that exists, not the bill that might exist.
  const [sovView, setSovView] = useState('billed'); // 'billed' | 'projected'

  // Search query for the redesigned action bar — filters lines by
  // sov_code, description, building_area_phase, or section_name.
  const [sovQuery, setSovQuery] = useState('');

  // Filter state for the Filter button popover. Two independent
  // dimensions, combined with AND. Empty = no constraint.
  //   - sectionFilter: Set of section_id (or '__ungrouped__'). Lines
  //     whose section_id is not in the set are hidden. Empty Set means
  //     'all sections'.
  //   - statusFilter: Set of strings, any of:
  //       'not_billed'       — earned == 0
  //       'partially_billed' — 0 < earned < contract
  //       'fully_billed'     — earned >= contract (within $0.01)
  //       'has_retainage'    — retainage_amount > 0 (net of released)
  //     Empty Set means 'all statuses'.
  const [sectionFilter, setSectionFilter] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState(new Set());

  // Sort state. `sortKey` references one of the SORT_OPTIONS below.
  // 'section' is the AIA-standard default (section then code).
  const [sortKey, setSortKey] = useState('section');

  // Popover open state for Filter / Sort buttons in the action bar.
  // Anchored to their trigger via getBoundingClientRect.
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);

  // Resizable columns
  const COL_RATIOS = [55, 195, 80, 100, 85, 85, 85, 85, 45, 85, 75, 75, 40];
  const COL_RATIO_SUM = COL_RATIOS.reduce((a, b) => a + b, 0);
  const COL_NAMES = ['Item No.', 'Description', 'Category', 'Sched. Value', 'Previous', 'This Period', 'Stored Mat.', 'Total', '%', 'Balance', 'Retainage', 'Released', ''];
  const COL_MIN = 40;
  const tableContainerRef = useRef(null);
  const [colWidths, setColWidths] = useState(COL_RATIOS);
  const resizingCol = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartW = useRef(0);
  const [activeResizeCol, setActiveResizeCol] = useState(null);
  const hasAutoSized = useRef(false);

  // Scale columns proportionally to fill container width
  const scaleColumnsToFit = useCallback((containerWidth) => {
    if (!containerWidth || containerWidth < 400) return;
    const available = containerWidth - 2; // account for border
    return COL_RATIOS.map(r => Math.max(COL_MIN, Math.round((r / COL_RATIO_SUM) * available)));
  }, []);

  // Auto-fit columns to container on mount and window resize
  useEffect(() => {
    const fitColumns = () => {
      if (resizingCol.current !== null) return; // don't interfere with manual resize
      const el = tableContainerRef.current;
      if (!el) return;
      const width = el.clientWidth;
      const scaled = scaleColumnsToFit(width);
      if (scaled) setColWidths(scaled);
    };

    // Use ResizeObserver so it reacts to container size changes (not just window)
    const el = tableContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      if (!hasAutoSized.current) {
        fitColumns();
        hasAutoSized.current = true;
      }
    });
    observer.observe(el);

    // Also fit on window resize
    const handleWindowResize = () => {
      if (resizingCol.current !== null) return;
      fitColumns();
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [scaleColumnsToFit]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (resizingCol.current === null) return;
      const diff = e.clientX - resizeStartX.current;
      const newW = Math.max(COL_MIN, resizeStartW.current + diff);
      setColWidths(prev => {
        const next = [...prev];
        next[resizingCol.current] = newW;
        return next;
      });
    };
    const onMouseUp = () => {
      if (resizingCol.current !== null) {
        resizingCol.current = null;
        setActiveResizeCol(null);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startColResize = useCallback((colIdx, e) => {
    e.preventDefault();
    resizingCol.current = colIdx;
    setActiveResizeCol(colIdx);
    resizeStartX.current = e.clientX;
    resizeStartW.current = colWidths[colIdx];
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths]);

  // Double-click resets that column to its proportional default based on current container width
  const resetColWidth = useCallback((colIdx) => {
    const el = tableContainerRef.current;
    if (!el) return;
    const available = el.clientWidth - 2;
    const defaultW = Math.max(COL_MIN, Math.round((COL_RATIOS[colIdx] / COL_RATIO_SUM) * available));
    setColWidths(prev => {
      const next = [...prev];
      next[colIdx] = defaultW;
      return next;
    });
  }, []);

  const gridTemplate = colWidths.map(w => `${w}px`).join(' ');
  const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);

  useEffect(() => {
    // GAAP cleanup: reset any stale progress from old auto-rollup, then load
    resetSOVProgress(projectId).catch(() => {}).finally(() => {
      fetchSOVLines();
      checkAllocation();
    });
  }, [projectId]);

  const fetchSOVLines = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSOVLines(projectId);
      setSovLines(data);
    } catch (err) {
      setError('Failed to fetch SOV lines');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkAllocation = async () => {
    try {
      const status = await checkSOVAllocation(projectId);
      setAllocationStatus(status);
    } catch (err) {
      console.error('Failed to check allocation:', err);
    }
  };

  const handleAddClick = () => {
    setFormData({
      sov_code: '',
      description: '',
      building_area_phase: '',
      billing_category: 'Labor',
      contract_amount: '',
      billing_method: 'Percent Complete',
      retainage_percent: 0,
      notes: '',
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contract_amount') {
      const raw = value.replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      const cleaned = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
      setFormData((prev) => ({
        ...prev,
        [name]: cleaned,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === 'retainage_percent' ? parseFloat(value) || 0 : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (creatingLine) return;
    try {
      setCreatingLine(true);
      setLoading(true);
      setError(null);
      await createSOVLine({
        ...formData,
        project_id: projectId
      });
      setShowModal(false);
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to create SOV line');
      console.error(err);
    } finally {
      setLoading(false);
      setCreatingLine(false);
    }
  };

  const handleUpdatePercent = async (sovLineId, percentComplete) => {
    try {
      await updateSOVLine(sovLineId, { percent_complete: parseFloat(percentComplete) || 0 });
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to update SOV line');
      console.error(err);
    }
  };

  const handleRollup = async (sovLineId) => {
    try {
      setLoading(true);
      await rollupSOVLine(sovLineId);
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to rollup SOV line');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRollupAll = async () => {
    try {
      setLoading(true);
      for (const line of sovLines) {
        await rollupSOVLine(line.id);
      }
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to rollup all SOV lines');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sovLineId) => {
    if (deletingId) return;
    const ok = await confirm({ title: 'Delete SOV Line', message: 'Are you sure you want to delete this SOV line? This action cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      setDeletingId(sovLineId);
      await deleteSOVLine(sovLineId);
      await fetchSOVLines();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      setError('Failed to delete SOV line');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatCurrencyInput = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    const parts = String(value).split('.');
    const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
    if (parts.length > 1) {
      return intPart + '.' + parts[1];
    }
    return intPart;
  };

  const getAllocationStatusClass = () => {
    if (!allocationStatus) return '';
    if (allocationStatus?.balanced) return 'allocation-balanced';
    if ((allocationStatus?.total_allocated || 0) > (allocationStatus?.total_contract || 0)) return 'allocation-warning';
    return 'allocation-under';
  };

  const getAllocationStatusColor = () => {
    if (!allocationStatus) return '#999';
    if (allocationStatus?.balanced) return '#4CAF50';
    if ((allocationStatus?.total_allocated || 0) > (allocationStatus?.total_contract || 0)) return '#F44336';
    return '#FFC107';
  };

  const getAllocationStatusText = () => {
    if (!allocationStatus) return 'Unknown';
    if (allocationStatus?.balanced) return 'BALANCED';
    if ((allocationStatus?.total_allocated || 0) > (allocationStatus?.total_contract || 0)) return 'OVER-ALLOCATED';
    return 'UNDER-ALLOCATED';
  };

  // Download XLSX template with Category Legend sheet
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // SOV Data sheet. section_name is the grouping that gets pushed to
    // CE (CREATE_SOV puts each line in the matching Setup-Schedule
    // group). Missing/blank means the line stays ungrouped in CE. New
    // section_name values are auto-created server-side at import time —
    // no need to pre-create sections in the UI.
    const headers = ['sov_code', 'description', 'section_name', 'building_area_phase', 'billing_category', 'contract_amount', 'billing_method', 'retainage_percent', 'notes'];
    const sampleRows = [
      ['SOV-001', 'High School Labor',    'High School', 'Building A', 'Labor',    150000, 'Percent Complete', 10, 'Example line item'],
      ['SOV-002', 'High School Material', 'High School', 'Building A', 'Material', 200000, 'Stored Materials', 10, ''],
      ['SOV-003', 'Junior High Labor',    'Junior High', 'Building B', 'Labor',    100000, 'Percent Complete', 10, ''],
    ];
    const wsData = [headers, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = [
      { wch: 12 }, { wch: 30 }, { wch: 18 }, { wch: 20 }, { wch: 15 },
      { wch: 18 }, { wch: 22 }, { wch: 18 }, { wch: 25 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'SOV Data');

    // Category Legend sheet
    const legendData = [
      ['FIELD', 'VALID OPTIONS / FORMAT'],
      [''],
      ['sov_code', 'Unique code for the line item (e.g., SOV-001)'],
      ['description', 'Short description of the line'],
      ['section_name', 'AFP section — groups this line on the AIA invoice (and in CE\'s Sorting-and-Printing tab). e.g., "Building A", "Sitework", "Concrete". Auto-created on import if new. Leave blank for ungrouped.'],
      ['building_area_phase', 'Free-form descriptive label on the line (e.g., "Phase 1"). Does NOT affect CE grouping — that\'s section_name above.'],
      ['billing_category', 'Labor, Material, Equipment, Other'],
      ['contract_amount', 'Number with no $ sign or commas (e.g., 150000)'],
      ['billing_method', 'Percent Complete, Manual, Spread Over Duration, Milestone, Stored Materials, Not Billable'],
      ['retainage_percent', 'Number between 0 and 100 (e.g., 10)'],
      ['notes', 'Free-form notes'],
    ];
    const wsLegend = XLSX.utils.aoa_to_sheet(legendData);
    wsLegend['!cols'] = [{ wch: 22 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(wb, wsLegend, 'Category Legend');

    XLSX.writeFile(wb, 'sov_template.xlsx');
  };

  // Parse rows from either CSV text or XLSX ArrayBuffer
  const parseFileRows = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm') {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      // Use first sheet (SOV Data) — skip Category Legend
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      return jsonData;
    } else {
      // CSV parsing
      const text = await file.text();
      const lines = text.split('\n').map(line => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      });

      if (lines.length < 2) return [];

      const headers = lines[0].map(h => h.toLowerCase().trim());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row || row.length < 2 || !row[0] || row[0].startsWith('---') || row[0].startsWith('billing_') || row[0].startsWith('retainage_') || row[0].startsWith('contract_')) continue;
        const rowData = {};
        headers.forEach((header, idx) => {
          if (idx < row.length) rowData[header] = row[idx];
        });
        rows.push(rowData);
      }
      return rows;
    }
  };

  // Import file (CSV, XLSX, XLS, XLSM)
  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError(null);

      const rows = await parseFileRows(file);
      if (rows.length === 0) {
        setError('File must have headers and at least one data row');
        return;
      }

      // Pre-filter to skip rows missing both sov_code and description so the
      // progress denominator reflects what we're actually going to send.
      const validRows = rows
        .map(rowData => {
          const r = {};
          Object.keys(rowData).forEach(k => { r[k.toLowerCase().trim()] = rowData[k]; });
          return r;
        })
        .filter(r => r.description || r.sov_code);

      if (validRows.length === 0) {
        setError('No importable rows found (every row was missing both sov_code and description).');
        return;
      }

      // Single bulk POST instead of one request per row. Avoids the
      // 500-req/min general rate limiter that previously 429'd page
      // navigation after a large import. Server wraps everything in a
      // transaction so we never get a half-imported SOV.
      setImportProgress({ current: 0, total: validRows.length, status: 'importing' });

      const payload = validRows.map(row => ({
        sov_code: String(row.sov_code || ''),
        description: String(row.description || ''),
        // section_name → server resolves to section_id, auto-creating
        // the section on this project if it doesn't exist yet. Empty/
        // missing means the line stays ungrouped in CE.
        section_name: String(row.section_name || '').trim(),
        building_area_phase: String(row.building_area_phase || ''),
        billing_category: String(row.billing_category || 'Labor'),
        contract_amount: parseFloat(row.contract_amount) || 0,
        billing_method: String(row.billing_method || 'Percent Complete'),
        retainage_percent: parseFloat(row.retainage_percent) || 0,
        notes: String(row.notes || ''),
      }));

      const result = await bulkCreateSOVLines(projectId, payload);
      const importCount = result?.count ?? validRows.length;

      await fetchSOVLines();
      // Sections may have been auto-created server-side when resolving
      // section_name values from the import. Refresh so the Properties-
      // modal dropdown shows them without a page reload.
      await fetchSections();
      await checkAllocation();
      if (refreshSummary) refreshSummary();
      // Show 'success' for ~1.2s then auto-dismiss the dialog.
      setImportProgress({ current: importCount, total: validRows.length, status: 'success' });
      // No auto-dismiss — per the LoadingDialog spec, the complete
      // state IS the receipt. User clicks Done to dismiss.
    } catch (err) {
      setError('Failed to import file: ' + err.message);
      console.error(err);
      // Leave the dialog up in error state so the user can read the message
      // and dismiss it manually.
      setImportProgress(p => ({
        current: p?.current || 0,
        total: p?.total || 0,
        status: 'error',
        error: err.message || String(err),
      }));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    // Negative horizontal margin escapes .app-main's 24px padding so
    // the SOV table can use the full chrome width. Padding restores
    // breathing room from the viewport edge. Matches the pattern in
    // BillingTab + ScheduleView so all three redesigned tabs sit
    // edge-to-edge consistently below the chrome above.
    <div className="sov-tab">
      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

      {allocationStatus && (() => {
        const total = allocationStatus.total_contract || 0;
        const allocated = allocationStatus.total_allocated || 0;
        const variance = allocationStatus.variance || 0;
        const allocPct = total > 0 ? (allocated / total) * 100 : 0;
        const isBalanced = allocationStatus.balanced;
        const isOver = allocated > total;
        const sovMatchesContract = allocationStatus.sov_matches_contract;
        const projectContractValue = allocationStatus.project_contract_value || 0;

        // Token-ified allocation status row. All bright-saturated greens /
        // ambers / reds replaced with --success / --warning / --danger
        // tokens. Status uses our existing StatusPill-style pattern: leading
        // dot + -soft bg + foreground color + 22%-mix ring.
        const cardStyle = {
          flex: 1, minWidth: 180, padding: '14px 16px',
          borderRadius: 10, border: '1px solid var(--border-subtle)',
          background: 'var(--bg-surface)',
          boxShadow: 'var(--shadow-sm)',
        };
        const labelStyle = {
          fontSize: 11.5, fontWeight: 500, textTransform: 'uppercase',
          letterSpacing: '0.04em', color: 'var(--text-3)', marginBottom: 6,
        };
        const valueStyle = {
          fontSize: 23.5, fontWeight: 500, color: 'var(--text-1)',
          letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
        };
        const subStyle = { fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 };
        const statusTone = isBalanced
          ? { fg: 'var(--success)', bg: 'var(--success-soft)' }
          : isOver
            ? { fg: 'var(--danger)', bg: 'var(--danger-soft)' }
            : { fg: 'var(--warning)', bg: 'var(--warning-soft)' };

        return (
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={cardStyle}>
              <div style={labelStyle}>Total Scheduled Value</div>
              <div className="mono" style={{ ...valueStyle, color: sovMatchesContract ? 'var(--success)' : 'var(--text-1)' }}>
                {formatCurrency(total)}
              </div>
              <div style={subStyle}>
                {sovLines.length} SOV line{sovLines.length !== 1 ? 's' : ''}
                {sovMatchesContract
                  ? <span style={{ marginLeft: 8, color: 'var(--success)', fontWeight: 500 }}>&#10003; Matches contract</span>
                  : total > 0 && projectContractValue > 0
                    ? <span className="mono" style={{ marginLeft: 8, color: 'var(--warning)', fontWeight: 500 }}>
                        {total > projectContractValue ? 'Over' : 'Under'} by {formatCurrency(Math.abs(total - projectContractValue))}
                      </span>
                    : null
                }
              </div>
            </div>
            <div style={cardStyle}>
              <div style={labelStyle}>Allocated to Tasks</div>
              <div className="mono" style={{
                ...valueStyle,
                color: isBalanced ? 'var(--success)' : isOver ? 'var(--danger)' : 'var(--text-1)',
              }}>
                {formatCurrency(allocated)}
              </div>
              <div className="mono" style={subStyle}>{allocPct.toFixed(1)}% of contract</div>
            </div>
            <div style={cardStyle}>
              <div style={labelStyle}>{isOver ? 'Over-Allocated' : 'Unallocated'}</div>
              <div className="mono" style={{
                ...valueStyle,
                color: isOver ? 'var(--danger)' : variance > 0 ? 'var(--warning)' : 'var(--success)',
              }}>
                {formatCurrency(Math.abs(variance))}
              </div>
              <div style={subStyle}>
                {isBalanced ? 'Fully allocated' : isOver ? 'exceeds contract' : 'remaining to link'}
              </div>
            </div>
            <div style={{
              ...cardStyle, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            }}>
              <div style={labelStyle}>Status</div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 999,
                fontSize: 13, fontWeight: 500,
                background: statusTone.bg, color: statusTone.fg,
                border: `1px solid color-mix(in oklch, ${statusTone.fg} 22%, transparent)`,
                marginTop: 4,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: 99, background: statusTone.fg, flexShrink: 0 }} />
                {getAllocationStatusText()}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Action bar — redesigned. Title + mono count on the left,
          search + ghost filter/sort + Billed/Projected segmented +
          icon-only utility buttons + +Section + +Line on the right. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 16, flexWrap: 'nowrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>
            Schedule of Values
          </span>
          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>
            {sovLines.length} line{sovLines.length === 1 ? '' : 's'}
          </span>
        </div>

        {/* Search field with leading icon + ⌘K hint */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', height: 28,
          padding: '0 8px', gap: 6,
          background: 'var(--bg-surface-2)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 6, minWidth: 180, maxWidth: 260, flex: '0 1 220px',
        }}>
          <Icon name="search" size={13} style={{ color: 'var(--text-3)' }} />
          <input
            type="text"
            placeholder="Search lines…"
            value={sovQuery}
            onChange={(e) => setSovQuery(e.target.value)}
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              color: 'var(--text-1)', fontFamily: 'var(--font-sans)', fontSize: 13.5, minWidth: 0,
            }}
          />
          {sovQuery
            ? <span onClick={() => setSovQuery('')} title="Clear" style={{ cursor: 'pointer', color: 'var(--text-3)', fontSize: 15.5, lineHeight: 1 }}>×</span>
            : <kbd className="mono" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                height: 18, padding: '0 5px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4, fontSize: 11.5, color: 'var(--text-3)',
              }}>⌘K</kbd>
          }
        </div>

        <SovGhostBtn
          icon="filter"
          label={
            (sectionFilter.size + statusFilter.size) > 0
              ? `Filter (${sectionFilter.size + statusFilter.size})`
              : 'Filter'
          }
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setFilterMenuAnchor(rect);
            setFilterMenuOpen((v) => !v);
            setSortMenuOpen(false);
          }}
        />
        <SovGhostBtn
          icon="sort"
          label={sortKey === 'section' ? 'Sort' : `Sort: ${SORT_OPTIONS.find(o => o.key === sortKey)?.label || ''}`}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setSortMenuAnchor(rect);
            setSortMenuOpen((v) => !v);
            setFilterMenuOpen(false);
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }} />

        {/* Billed / Projected segmented (replaces the bright pill toggle) */}
        <SegmentedControl
          value={sovView}
          onChange={setSovView}
          options={[
            { value: 'projected', label: 'Projected' },
            { value: 'billed', label: 'Billed' },
          ]}
        />

        {/* Icon-only utility buttons */}
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <SovIconBtn name="template" label="Download template" onClick={handleDownloadTemplate} />
          <label
            title="Import SOV lines" aria-label="Import SOV lines"
            style={{
              width: 28, height: 28, borderRadius: 6, border: 'none',
              background: 'transparent', color: 'var(--text-3)',
              cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', padding: 0,
              transition: 'background .12s, color .12s', marginBottom: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
          >
            <Icon name="upload" size={14} />
            <input type="file" accept=".xlsx,.xls,.xlsm,.csv" onChange={handleImportFile} style={{ display: 'none' }} />
          </label>
        </div>

        {/* Sections are auto-created via import (section_name column) and
            assigned per-line via the Properties modal. No dedicated
            +Section action yet — clicking opens a hint about that flow. */}
        <button
          className="btn btn-secondary"
          onClick={() => alert('Sections are created automatically when you import a template with a section_name column, or assigned per-line via the line Properties dialog.')}
          style={{ padding: '5px 10px', fontSize: 13.5, whiteSpace: 'nowrap' }}
        >
          + Section
        </button>
        <button className="btn btn-primary" onClick={handleAddClick} disabled={loading}
          style={{ padding: '5px 12px', fontSize: 13.5, whiteSpace: 'nowrap' }}>
          + Line
        </button>
      </div>

      {/* Subtitle moved out of toolbar — provides context for which view is active. */}
      <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: -8, marginBottom: 16 }}>
        {sovView === 'billed'
          ? 'Showing finalized billing — values from last exported billing cycle.'
          : 'Showing projected — live values from current task progress.'}
      </div>

      {/* SOV import — themed LoadingDialog. Status maps cleanly:
            'importing' → 'progress', 'success' → 'complete',
            'error' → 'error'. Dialog stays mounted across the
            transition; per spec, complete state requires the user
            to click Done (no auto-dismiss). */}
      <LoadingDialog
        open={!!importProgress}
        state={
          importProgress?.status === 'success' ? 'complete'
            : importProgress?.status === 'error' ? 'error'
            : 'progress'
        }
        noun="SOV lines"
        current={importProgress?.current || 0}
        total={importProgress?.total || 0}
        errorMessage={importProgress?.error}
        cancellable={false}
        onDismiss={() => setImportProgress(null)}
        onRetry={() => setImportProgress(null)}
      />

      {showModal && (() => {
        const mInput = {
          width: '100%', padding: '10px 14px', fontSize: '15.5px',
          border: `1px solid ${t.inputBorder}`, borderRadius: '10px', outline: 'none',
          transition: 'border-color 0.2s', backgroundColor: t.inputBg, color: t.inputColor, boxSizing: 'border-box',
        };
        const mLabel = { display: 'block', fontSize: '15.5px', fontWeight: 600, color: t.heading, marginBottom: '6px' };
        const mGroup = { marginBottom: '14px' };
        return (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: t.modalBg, borderRadius: '16px', width: '520px', maxHeight: '90vh',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.1)',
              animation: 'slideUp 0.25s ease',
            }}>
              {/* Header */}
              <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '19.5px', fontWeight: 700, color: t.heading }}>Add SOV Line</h2>
                    <p style={{ margin: '6px 0 0', fontSize: '14.5px', color: t.textMuted }}>Create a new schedule of values line item.</p>
                  </div>
                  <button onClick={() => setShowModal(false)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: t.textMuted, fontSize: '23.5px', lineHeight: 1, borderRadius: '6px',
                  }}>&times;</button>
                </div>
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div style={mGroup}>
                    <label style={mLabel}>SOV Code *</label>
                    <input type="text" name="sov_code" value={formData.sov_code} onChange={handleFormChange} required placeholder="e.g., SOV-001" style={mInput}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                  <div style={mGroup}>
                    <label style={mLabel}>Billing Category *</label>
                    <select name="billing_category" value={formData.billing_category} onChange={handleFormChange} style={mInput}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Equipment</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Description *</label>
                  <input type="text" name="description" value={formData.description} onChange={handleFormChange} required placeholder="Description of work" style={mInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Building / Area / Phase</label>
                  <input type="text" name="building_area_phase" value={formData.building_area_phase} onChange={handleFormChange} placeholder="e.g., High School" style={mInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div style={mGroup}>
                    <label style={mLabel}>Scheduled Value *</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '15.5px', pointerEvents: 'none' }}>$</span>
                      <input type="text" name="contract_amount" value={formatCurrencyInput(formData.contract_amount)} onChange={handleFormChange} required placeholder="0.00"
                        style={{ ...mInput, paddingLeft: '28px' }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                    </div>
                  </div>
                  <div style={mGroup}>
                    <label style={mLabel}>Retainage %</label>
                    <input type="number" name="retainage_percent" value={formData.retainage_percent} onChange={handleFormChange} placeholder="0" step="0.1" min="0" max="100" style={mInput}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Billing Method *</label>
                  <select name="billing_method" value={formData.billing_method} onChange={handleFormChange} style={mInput}>
                    <option>Percent Complete</option>
                    <option>Manual</option>
                    <option>Spread Over Duration</option>
                    <option>Milestone</option>
                    <option>Stored Materials</option>
                    <option>Not Billable</option>
                  </select>
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleFormChange} placeholder="Additional notes" rows="3"
                    style={{ ...mInput, resize: 'vertical', minHeight: '70px' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: `1px solid ${t.tableBorder}` }}>
                  <button type="button" onClick={() => setShowModal(false)} disabled={loading || creatingLine}
                    style={{
                      padding: '10px 20px', borderRadius: '10px', fontSize: '15.5px', fontWeight: 600,
                      border: `1px solid ${t.inputBorder}`, backgroundColor: t.bg, color: t.text,
                      cursor: (loading || creatingLine) ? 'not-allowed' : 'pointer',
                      opacity: (loading || creatingLine) ? 0.6 : 1,
                    }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={loading || creatingLine}
                    style={{
                      padding: '10px 20px', borderRadius: '10px', fontSize: '15.5px', fontWeight: 600,
                      border: 'none', backgroundColor: '#3b82f6', color: '#fff',
                      cursor: creatingLine ? 'wait' : 'pointer',
                      opacity: (loading || creatingLine) ? 0.8 : 1,
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}>
                    {creatingLine && (
                      <span style={{
                        width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                        borderTopColor: '#fff', borderRadius: '50%',
                        animation: 'spin 0.7s linear infinite', display: 'inline-block',
                      }} />
                    )}
                    {creatingLine ? 'Creating…' : 'Create SOV Line'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* SOV Lines Table */}
      <div ref={tableContainerRef} style={{
        border: '1px solid var(--border-subtle)', borderRadius: 12,
        background: 'var(--bg-surface)',
        // Bounded scroll pane so ONLY the line items scroll: the sticky
        // column header + sticky totals stay pinned while rows move, and
        // the (already-sticky) page chrome stays put. Fills the viewport
        // height, floored so short laptops don't regress. overflowX keeps
        // the wide 12-col grid horizontally scrollable.
        overflowX: 'auto', overflowY: 'auto',
        maxHeight: 'max(420px, calc(100vh - 380px))',
        boxShadow: 'var(--shadow-sm)',
      }}>
        {/* Table Header — token-ified per redesign. Mono uppercase
            eyebrows at --text-3 / 0.04em letter-spacing match the rest
            of the redesigned chrome. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          padding: '12px 0',
          position: 'sticky', top: 0, zIndex: 4,
          background: 'var(--bg-surface-2)',
          borderBottom: '1px solid var(--border-subtle)',
          fontSize: 12, fontWeight: 500, textTransform: 'uppercase',
          letterSpacing: '0.04em', color: 'var(--text-3)',
          minWidth: totalTableWidth,
        }}>
          {COL_NAMES.map((name, i) => (
            <div key={i} style={{
              position: 'relative', padding: '0 10px',
              textAlign: i >= 3 && i !== 8 ? 'right' : i === 8 ? 'center' : 'left',
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
            }}>
              {name}
              {i < COL_NAMES.length - 1 && (
                <div
                  onMouseDown={(e) => startColResize(i, e)}
                  onDoubleClick={() => resetColWidth(i)}
                  title="Drag to resize, double-click to reset"
                  style={{
                    position: 'absolute', right: '-4px', top: 0, bottom: 0, width: '9px',
                    cursor: 'col-resize', zIndex: 5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.08)';
                    e.currentTarget.querySelector('.resize-line').style.backgroundColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    if (resizingCol.current === null) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.querySelector('.resize-line').style.backgroundColor = '#cbd5e1';
                    }
                  }}
                >
                  <div className="resize-line" style={{
                    width: '1px', height: activeResizeCol === i ? '100%' : '60%',
                    backgroundColor: activeResizeCol === i ? '#3b82f6' : '#cbd5e1',
                    borderRadius: '1px',
                    transition: 'height 0.15s ease, background-color 0.15s ease',
                  }} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Rows */}
        {loading && sovLines.length === 0 ? (
          <LoadingState message="Loading SOV lines…" />
        ) : sovLines.length === 0 ? (
          <StateScreen
            kind="empty"
            title="No SOV lines yet"
            body="Add line items to build out this project's schedule of values, or import them from an Excel template."
            primary={{ label: 'Add a line', icon: 'plus', onClick: handleAddClick }}
            secondary={{ label: 'Download template', icon: 'download', onClick: handleDownloadTemplate }}
          />
        ) : (() => {
          // Billed view used to short-circuit to an empty-state screen
          // when nothing had been finalized yet. Per user preference we
          // now always render the SOV table -- unbilled lines just show
          // $0 / 0% across the board, which matches how Billed values
          // look anyway. Keeps the layout stable and lets PMs see the
          // line items they're about to bill against. The Projected
          // toggle is still one click away if they want the forecast.
          // ───── Search + Filter ─────
          // Three layers, AND-combined:
          //   1. Search box (sovQuery) — matches code / description /
          //      building_area_phase / section_name.
          //   2. Section filter (sectionFilter Set) — line's section_id
          //      must be in the set. '__ungrouped__' covers null section_id.
          //      Empty Set = no constraint.
          //   3. Status filter (statusFilter Set) — line's billing state
          //      must be in the set. Empty Set = no constraint.
          const q = sovQuery.trim().toLowerCase();
          const sectionLookup = Object.fromEntries(
            sections.map((s) => [s.id, s])
          );
          // Helpers that classify a line by billing status + compute
          // values used by the sort comparators below. Shared so the
          // same earned figure drives filter, sort, AND row rendering.
          const earnedOf = (l) => {
            const isBilled = sovView === 'billed';
            return isBilled
              ? (parseFloat(l.last_billed_earned) || 0)
              : (parseFloat(l.earned_amount) || 0);
          };
          const pctOf = (l) => {
            const c = parseFloat(l.contract_amount) || 0;
            return c > 0 ? (earnedOf(l) / c) * 100 : 0;
          };
          const balanceOf = (l) => {
            const c = parseFloat(l.contract_amount) || 0;
            return c - earnedOf(l);
          };
          const lineStatus = (l) => {
            const c = parseFloat(l.contract_amount) || 0;
            const e = earnedOf(l);
            if (c > 0 && e >= c - 0.01) return 'fully_billed';
            if (e > 0.01) return 'partially_billed';
            return 'not_billed';
          };
          const lineHasRetainage = (l) => (parseFloat(l.retainage_amount) || 0) > 0.01;

          const visible = sovLines.filter((l) => {
            // Layer 1 — search
            if (q) {
              const sectionName = sectionLookup[l.section_id]?.section_name || '';
              const matches =
                (l.sov_code || '').toLowerCase().includes(q) ||
                (l.description || '').toLowerCase().includes(q) ||
                (l.building_area_phase || '').toLowerCase().includes(q) ||
                sectionName.toLowerCase().includes(q);
              if (!matches) return false;
            }
            // Layer 2 — section filter
            if (sectionFilter.size > 0) {
              const key = l.section_id ? String(l.section_id) : '__ungrouped__';
              if (!sectionFilter.has(key)) return false;
            }
            // Layer 3 — status filter
            if (statusFilter.size > 0) {
              const s = lineStatus(l);
              const inStatusSet = statusFilter.has(s);
              const matchesRetainage =
                statusFilter.has('has_retainage') && lineHasRetainage(l);
              if (!inStatusSet && !matchesRetainage) return false;
            }
            return true;
          });
          if (visible.length === 0) {
            const hasAnyFilter = q || sectionFilter.size > 0 || statusFilter.size > 0;
            if (hasAnyFilter) {
              return (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 14.5 }}>
                  {q
                    ? `No lines match "${sovQuery}"`
                    : 'No lines match the current filters.'}
                  {(sectionFilter.size > 0 || statusFilter.size > 0) && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={() => { setSectionFilter(new Set()); setStatusFilter(new Set()); }}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          color: 'var(--accent-text)', cursor: 'pointer',
                          textDecoration: 'underline', font: 'inherit',
                        }}
                      >Clear filters</button>
                    </>
                  )}
                </div>
              );
            }
          }
          // ───── Sort ─────
          // SORT_OPTIONS lives at module scope so the catalog is stable.
          // Final tiebreaker = the AIA default (section then code) so
          // rows with equal sort values still group sensibly.
          const sortOpt = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];
          const defaultOpt = SORT_OPTIONS[0];
          const ctx = { sectionLookup, pctOf, balanceOf };
          const sortedLines = visible.slice().sort((a, b) => {
            const primary = sortOpt.cmp(a, b, ctx);
            if (primary !== 0) return primary;
            return sortOpt === defaultOpt ? 0 : defaultOpt.cmp(a, b, ctx);
          });
          // ───── Per-section subtotals (precomputed for header rendering) ─────
          const sectionSubtotals = {};
          sortedLines.forEach((line) => {
            const k = line.section_id || '__ungrouped__';
            if (!sectionSubtotals[k]) sectionSubtotals[k] = { scheduled: 0, billed: 0, count: 0 };
            sectionSubtotals[k].scheduled += parseFloat(line.contract_amount) || 0;
            const isBilledView = sovView === 'billed';
            const earned = isBilledView
              ? (parseFloat(line.last_billed_earned) || 0)
              : (parseFloat(line.earned_amount) || 0);
            sectionSubtotals[k].billed += earned;
            sectionSubtotals[k].count += 1;
          });
          return sortedLines.map((line, idx, arr) => {
            const prev = idx > 0 ? arr[idx - 1] : null;
            const isNewSection = !prev || prev.section_id !== line.section_id;
            const sectionRecord = sectionLookup[line.section_id];
            const sectionName = sectionRecord?.section_name || '(Ungrouped)';
            const sectionKey = line.section_id || '__ungrouped__';
            const subtotal = sectionSubtotals[sectionKey];
              const contractAmt = parseFloat(line.contract_amount) || 0;
              const retainagePct = parseFloat(line.retainage_percent) || 0;
              const retainageReleased = parseFloat(line.retainage_released) || 0;
              const storedMat = parseFloat(line.stored_materials_amount) || 0;

              // Billed view: from last finalized billing export
              // Projected view: live from current task progress
              const isBilled = sovView === 'billed';
              const pct = isBilled
                ? (parseFloat(line.last_billed_percent) || 0)
                : (parseFloat(line.percent_complete) || 0);
              const earnedAmt = isBilled
                ? (parseFloat(line.last_billed_earned) || 0)
                : (parseFloat(line.earned_amount) || 0);
              // Billed view: "Previous" = what was billed before the last finalized period
              // Projected view: "Previous" = everything already officially billed (cumulative)
              const prevBilled = isBilled
                ? (parseFloat(line.previous_billed_amount) || 0)
                : (parseFloat(line.cumulative_billed) || 0);
              // "This Period" = gross earned delta beyond what's already been billed
              const curBilled = Math.max(0, earnedAmt - prevBilled);
              const total = earnedAmt + storedMat;
              const balance = contractAmt - total;
              const retainageAmt = (total * retainagePct) / 100;
              const billedDate = line.last_billed_date;
              const hasBilledData = (parseFloat(line.last_billed_percent) || 0) > 0 || (parseFloat(line.cumulative_billed) || 0) > 0;
              const isOverbilled = total > contractAmt && contractAmt > 0;

              return (
                <React.Fragment key={line.id}>
                {isNewSection && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: gridTemplate,
                    minWidth: totalTableWidth, padding: '10px 0',
                    background: 'var(--bg-surface-2)',
                    borderTop: '1px solid var(--border-subtle)',
                    borderBottom: '1px solid var(--border-subtle)',
                    alignItems: 'center',
                  }}>
                    <div style={{ padding: '0 10px' }}></div>
                    {/* Section name + color dot + count span the next few columns */}
                    <div style={{
                      gridColumn: 'span 3',
                      padding: '0 10px',
                      display: 'flex', alignItems: 'center', gap: 8,
                      minWidth: 0,
                    }}>
                      <span aria-hidden style={{
                        width: 6, height: 6, borderRadius: 999,
                        background: sovSectionColor(sectionName),
                        flexShrink: 0,
                      }} />
                      <span style={{
                        fontSize: 13.5, fontWeight: 600,
                        color: 'var(--text-1)', letterSpacing: '-0.01em',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {sectionName}
                      </span>
                      <span className="mono" style={{
                        fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)',
                        flexShrink: 0,
                      }}>
                        {subtotal.count}
                      </span>
                    </div>
                    {/* Right side: scheduled + billed subtotals span the rest */}
                    <div className="mono" style={{
                      gridColumn: 'span 9',
                      padding: '0 10px',
                      fontSize: 12.5, color: 'var(--text-3)',
                      display: 'flex', justifyContent: 'flex-end', gap: 14,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      <span>
                        <span style={{ color: 'var(--text-4)', marginRight: 4 }}>Scheduled</span>
                        <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{formatCurrency(subtotal.scheduled)}</span>
                      </span>
                      <span>
                        <span style={{ color: 'var(--text-4)', marginRight: 4 }}>Billed</span>
                        <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{formatCurrency(subtotal.billed)}</span>
                      </span>
                    </div>
                  </div>
                )}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  padding: '14px 0',
                  borderBottom: idx < arr.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  alignItems: 'center',
                  transition: 'background-color 0.1s',
                  minWidth: totalTableWidth,
                  // Over-billed: inset accent stripe (boxShadow doesn't
                  // consume column width like a border would).
                  boxShadow: isOverbilled ? 'inset 3px 0 0 var(--danger)' : 'none',
                }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {/* Item No. — mono identifier in muted text. Double-click
                      opens properties. Was bright blue --link color. */}
                  <div
                    onDoubleClick={() => openPropsModal(line)}
                    className="mono"
                    style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-4)', letterSpacing: '-0.02em', padding: '0 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                    title="Double-click to edit properties"
                  >
                    {line.sov_code}
                  </div>

                  {/* Description (+ building area / phase as secondary line) */}
                  <div style={{ padding: '0 10px', overflow: 'hidden', minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.description}</div>
                    {line.building_area_phase && (
                      <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.building_area_phase}</div>
                    )}
                  </div>

                  {/* Category — subtle pill (style derived per-category) */}
                  <div style={{ padding: '0 10px', overflow: 'hidden' }}>
                    <span style={{
                      ...getCategoryStyle(line.billing_category),
                      padding: '2px 8px', borderRadius: 999,
                      fontSize: 12, fontWeight: 500,
                      display: 'inline-block', whiteSpace: 'nowrap',
                    }}>{line.billing_category}</span>
                  </div>

                  {/* Scheduled Value */}
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, color: 'var(--text-1)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', padding: '0 10px' }}>
                    {formatCurrency(contractAmt)}
                  </div>

                  {/* Previous */}
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, color: 'var(--text-3)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', padding: '0 10px' }}>
                    {formatCurrency(prevBilled)}
                  </div>

                  {/* This Period */}
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', padding: '0 10px',
                    color: isBilled ? 'var(--text-1)' : 'var(--accent-text)',
                    fontStyle: isBilled ? 'normal' : 'italic',
                  }}>
                    {formatCurrency(curBilled)}
                    {!isBilled && curBilled > 0 && <span style={{ fontSize: 10.5, marginLeft: 3, color: 'var(--text-4)', fontStyle: 'normal' }}>est</span>}
                  </div>

                  {/* Stored Materials */}
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, color: storedMat > 0 ? 'var(--text-1)' : 'var(--text-4)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', padding: '0 10px' }}>
                    {formatCurrency(storedMat)}
                  </div>

                  {/* Total — bolder weight; this is the column that drives % + balance */}
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', padding: '0 10px' }}>
                    {formatCurrency(total)}
                  </div>

                  {/* % — mono with tabular-nums so multi-digit values align */}
                  <div className="mono" style={{ textAlign: 'center', fontSize: 14, fontWeight: 500, padding: '0 10px',
                    color: isBilled ? 'var(--text-2)' : 'var(--accent-text)',
                    fontStyle: isBilled ? 'normal' : 'italic',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {contractAmt > 0 ? Math.round(pct) : 0}
                    {!isBilled && <span style={{ fontSize: 10.5, color: 'var(--text-4)', fontStyle: 'normal' }}>%</span>}
                    {isBilled && '%'}
                  </div>

                  {/* Balance — success when zero (line fully completed), danger
                      when negative (over-billed), neutral otherwise. */}
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14,
                    fontWeight: balance === 0 && contractAmt > 0 ? 600 : 500,
                    color: balance === 0 && contractAmt > 0 ? 'var(--success)' : balance >= 0 ? 'var(--text-1)' : 'var(--danger)',
                    letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums',
                    padding: '0 10px',
                  }}>
                    {formatCurrency(balance)}
                  </div>

                  {/* Retainage held — token warning color when > 0, muted otherwise */}
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, color: retainageAmt > 0 ? 'var(--warning)' : 'var(--text-4)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', padding: '0 10px' }}>
                    {formatCurrency(retainageAmt)}
                  </div>

                  {/* Released — success when partially released, "Full" chip when fully released */}
                  <div style={{ textAlign: 'right', fontSize: 13.5, padding: '0 6px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    {retainageReleased > 0 ? (
                      <span className="mono" style={{ color: 'var(--success)', fontWeight: 600, fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(retainageReleased)}</span>
                    ) : null}
                    {retainageAmt > 0 && retainageReleased === 0 && (
                      <span style={{ color: 'var(--text-4)', fontSize: 11.5 }}>–</span>
                    )}
                    {retainageAmt > 0 && retainageAmt <= retainageReleased && (
                      <span style={{ color: 'var(--success)', fontSize: 11.5, fontWeight: 600 }}>Full</span>
                    )}
                  </div>

                  {/* Delete */}
                  <div style={{ textAlign: 'center', padding: '0 4px' }}>
                    <button
                      onClick={() => handleDelete(line.id)}
                      disabled={loading || deletingId === line.id || (deletingId && deletingId !== line.id)}
                      title={deletingId === line.id ? 'Deleting…' : 'Delete this SOV line'}
                      style={{
                        background: 'none', border: 'none',
                        cursor: deletingId === line.id ? 'wait' : (loading || deletingId ? 'not-allowed' : 'pointer'),
                        opacity: (loading || deletingId) ? 0.5 : 1,
                        padding: '4px',
                        color: '#cbd5e1', fontSize: '17.5px', lineHeight: 1, borderRadius: '6px',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { if (!deletingId && !loading) { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.backgroundColor = '#fef2f2'; } }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {deletingId === line.id ? (
                        <span style={{
                          width: 14, height: 14, border: '2px solid rgba(0,0,0,0.1)',
                          borderTopColor: '#ef4444', borderRadius: '50%',
                          animation: 'spin 0.7s linear infinite', display: 'inline-block',
                        }} />
                      ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                </React.Fragment>
              );
            });
        })()}

        {/* Totals Row */}
        {sovLines.length > 0 && (() => {
          const isBilled = sovView === 'billed';
          const totalContract = sovLines.reduce((s, l) => s + (parseFloat(l.contract_amount) || 0), 0);
          const totalPrevBilled = isBilled
            ? sovLines.reduce((s, l) => s + (parseFloat(l.previous_billed_amount) || 0), 0)
            : sovLines.reduce((s, l) => s + (parseFloat(l.cumulative_billed) || 0), 0);
          const totalStoredMat = sovLines.reduce((s, l) => s + (parseFloat(l.stored_materials_amount) || 0), 0);

          // "This Period" always shows gross earned delta, not net after retainage
          const totalEarned = isBilled
            ? sovLines.reduce((s, l) => s + (parseFloat(l.last_billed_earned) || 0), 0)
            : sovLines.reduce((s, l) => s + (parseFloat(l.earned_amount) || 0), 0);
          const totalCurBilled = Math.max(0, totalEarned - totalPrevBilled);
          const grandTotal = totalEarned + totalStoredMat;

          const totalBalance = totalContract - grandTotal;
          const totalRetainage = sovLines.reduce((s, l) => {
            const amt = parseFloat(l.contract_amount) || 0;
            const pctVal = isBilled ? (parseFloat(l.last_billed_percent) || 0) : (parseFloat(l.percent_complete) || 0);
            const earned = amt * pctVal / 100;
            const stored = parseFloat(l.stored_materials_amount) || 0;
            const retPct = parseFloat(l.retainage_percent) || 0;
            return s + ((earned + stored) * retPct / 100);
          }, 0);
          const totalReleased = sovLines.reduce((s, l) => s + (parseFloat(l.retainage_released) || 0), 0);
          const overallPct = totalContract > 0 ? Math.round((grandTotal / totalContract) * 100) : 0;
          return (
            <div style={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              padding: '12px 0',
              position: 'sticky', bottom: 0, zIndex: 3,
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface-2)',
              fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)',
              alignItems: 'center',
              minWidth: totalTableWidth,
              letterSpacing: '-0.005em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <div style={{ padding: '0 10px' }}></div>
              <div style={{ padding: '0 10px' }}>
                <span style={{
                  fontSize: 11.5, fontWeight: 500, letterSpacing: '0.04em',
                  textTransform: 'uppercase', color: 'var(--text-3)', marginRight: 6,
                }}>Total</span>
                <span className="mono" style={{ color: 'var(--text-2)', fontSize: 12.5 }}>{sovLines.length} line{sovLines.length === 1 ? '' : 's'}</span>
                {!isBilled && <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--accent-text)', marginLeft: 6, fontStyle: 'italic' }}>projected</span>}
              </div>
              <div style={{ padding: '0 10px' }}></div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalContract)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalPrevBilled)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px', color: isBilled ? t.heading : '#6366f1', fontStyle: isBilled ? 'normal' : 'italic' }}>{formatCurrency(totalCurBilled)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalStoredMat)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(grandTotal)}</div>
              <div style={{ textAlign: 'center', padding: '0 10px' }}>{overallPct}%</div>
              <div style={{ textAlign: 'right', padding: '0 10px' }}>{formatCurrency(totalBalance)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px', color: '#d97706' }}>{formatCurrency(totalRetainage)}</div>
              <div style={{ textAlign: 'right', padding: '0 10px', color: totalReleased > 0 ? '#16a34a' : undefined }}>{totalReleased > 0 ? formatCurrency(totalReleased) : '–'}</div>
              <div style={{ padding: '0 10px' }}></div>
            </div>
          );
        })()}
      </div>
      <ConfirmDialog {...confirmDialogProps} />

      {/* ═══ Filter popover ═══
          Two grouped sections: SECTION (one checkbox per section on
          this project, plus 'Ungrouped' for null section_id) and
          STATUS (Not billed / Partially billed / Fully billed / Has
          retainage held). Clicking a row toggles its inclusion in
          the corresponding Set without closing the popover. */}
      <SovPopover
        open={filterMenuOpen}
        anchor={filterMenuAnchor}
        onClose={() => setFilterMenuOpen(false)}
        minWidth={240}
      >
        {/* SECTION group */}
        <div style={{
          padding: '6px 8px 4px', fontSize: 11.5, fontWeight: 600,
          color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>Section</span>
          {sectionFilter.size > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSectionFilter(new Set()); }}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: 'var(--accent-text)', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
              }}
            >Clear</button>
          )}
        </div>
        {(() => {
          // Distinct section list = every section_id present on a line
          // (this project's sections come from the `sections` table but
          // we only show ones actually used, to avoid offering empty
          // filter slots). Always include 'Ungrouped' if any line has
          // a null section_id.
          const used = new Map();
          let hasUngrouped = false;
          for (const l of sovLines) {
            if (!l.section_id) { hasUngrouped = true; continue; }
            const s = sections.find((x) => x.id === l.section_id);
            if (s) used.set(String(s.id), s.section_name);
          }
          const opts = Array.from(used.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([key, name]) => ({ key, label: name }));
          if (hasUngrouped) opts.push({ key: '__ungrouped__', label: '(Ungrouped)' });
          if (opts.length === 0) {
            return (
              <div style={{ padding: '6px 10px', fontSize: 13.5, color: 'var(--text-4)' }}>
                No sections in use.
              </div>
            );
          }
          return opts.map((opt) => {
            const checked = sectionFilter.has(opt.key);
            return (
              <FilterCheckRow
                key={`sec-${opt.key}`}
                checked={checked}
                label={opt.label}
                onClick={() => {
                  setSectionFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(opt.key)) next.delete(opt.key);
                    else next.add(opt.key);
                    return next;
                  });
                }}
              />
            );
          });
        })()}

        <div style={{ height: 1, background: 'var(--border-subtle)', margin: '4px 6px' }} />

        {/* STATUS group */}
        <div style={{
          padding: '6px 8px 4px', fontSize: 11.5, fontWeight: 600,
          color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>Status</span>
          {statusFilter.size > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setStatusFilter(new Set()); }}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: 'var(--accent-text)', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
              }}
            >Clear</button>
          )}
        </div>
        {[
          { key: 'not_billed',       label: 'Not billed yet' },
          { key: 'partially_billed', label: 'Partially billed' },
          { key: 'fully_billed',     label: 'Fully billed' },
          { key: 'has_retainage',    label: 'Has retainage held' },
        ].map((opt) => {
          const checked = statusFilter.has(opt.key);
          return (
            <FilterCheckRow
              key={`st-${opt.key}`}
              checked={checked}
              label={opt.label}
              onClick={() => {
                setStatusFilter((prev) => {
                  const next = new Set(prev);
                  if (next.has(opt.key)) next.delete(opt.key);
                  else next.add(opt.key);
                  return next;
                });
              }}
            />
          );
        })}
      </SovPopover>

      {/* ═══ Sort popover ═══
          Single-select radio list. Clicking a row sets sortKey and
          closes the popover. */}
      <SovPopover
        open={sortMenuOpen}
        anchor={sortMenuAnchor}
        onClose={() => setSortMenuOpen(false)}
        minWidth={240}
      >
        <div style={{
          padding: '6px 8px 4px', fontSize: 11.5, fontWeight: 600,
          color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>Sort by</div>
        {SORT_OPTIONS.map((opt) => {
          const selected = sortKey === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSortKey(opt.key);
                setSortMenuOpen(false);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '7px 10px',
                border: 'none',
                background: selected ? 'var(--accent-soft)' : 'transparent',
                color: selected ? 'var(--accent-text)' : 'var(--text-1)',
                cursor: 'pointer', borderRadius: 5,
                textAlign: 'left', fontSize: 14, fontWeight: 500,
                transition: 'background .1s',
              }}
              onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                width: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--accent-text)',
              }}>
                {selected && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </SovPopover>

      {/* ═══ SOV LINE PROPERTIES MODAL ═══ */}
      {propsModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={(e) => { if (e.target === e.currentTarget) setPropsModal(null); }}
        >
          <div style={{ backgroundColor: t.bg, borderRadius: 16, width: 620, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${t.cardBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 19.5, fontWeight: 700, color: t.heading }}>{propsModal.sov_code} Properties</h2>
                <p style={{ margin: '4px 0 0', fontSize: 14.5, color: t.textSecondary }}>{propsModal.description}</p>
              </div>
              <button onClick={() => setPropsModal(null)} style={{ background: 'none', border: 'none', fontSize: 23.5, color: t.textSecondary, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {/* Editable fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>SOV Code</label>
                  <input value={propsForm.sov_code} onChange={e => setPropsForm(f => ({ ...f, sov_code: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Category</label>
                  <select value={propsForm.billing_category} onChange={e => setPropsForm(f => ({ ...f, billing_category: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }}>
                    <option>Labor</option><option>Material</option><option>General Conditions</option><option>Equipment</option><option>Other</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Description</label>
                  <input value={propsForm.description} onChange={e => setPropsForm(f => ({ ...f, description: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Building Area / Phase</label>
                  <input value={propsForm.building_area_phase} onChange={e => setPropsForm(f => ({ ...f, building_area_phase: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>
                    AFP Section
                    <span style={{ fontWeight: 400, color: t.textSecondary, marginLeft: 6, fontSize: 12.5 }}>
                      (groups this line on the AIA invoice and in CE's Sorting-and-Printing tab)
                    </span>
                  </label>
                  <select
                    value={propsForm.section_id == null ? '' : String(propsForm.section_id)}
                    onChange={async (e) => {
                      const v = e.target.value;
                      if (v === '__new__') {
                        const name = (window.prompt('New section name:') || '').trim();
                        if (!name) return;
                        try {
                          const created = await createSection({ project_id: projectId, section_name: name });
                          await fetchSections();
                          setPropsForm(f => ({ ...f, section_id: created.id }));
                        } catch (err) {
                          window.alert('Failed to create section: ' + (err.message || err));
                        }
                        return;
                      }
                      setPropsForm(f => ({ ...f, section_id: v === '' ? null : parseInt(v, 10) }));
                    }}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }}
                  >
                    <option value="">— Unsectioned —</option>
                    {sections.map(s => (
                      <option key={s.id} value={s.id}>{s.section_name}</option>
                    ))}
                    <option value="__new__">+ Add new section…</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Contract Amount</label>
                  <input type="number" step="0.01" value={propsForm.contract_amount} onChange={e => setPropsForm(f => ({ ...f, contract_amount: parseFloat(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                </div>
              </div>

              {/* Retainage section */}
              <div style={{ padding: 16, border: `1px solid ${t.cardBorder}`, borderRadius: 12, marginBottom: 20, backgroundColor: t.bgAlt }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: t.heading, marginBottom: 12 }}>Retention Settings</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Retention for Work (%)</label>
                    <input type="number" step="0.1" min="0" max="100" value={propsForm.retainage_percent}
                      onChange={e => setPropsForm(f => ({ ...f, retainage_percent: parseFloat(e.target.value) || 0 }))}
                      style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Retention for Stored Material (%)</label>
                    <input type="number" step="0.1" min="0" max="100" value={propsForm.retainage_percent_stored}
                      onChange={e => setPropsForm(f => ({ ...f, retainage_percent_stored: parseFloat(e.target.value) || 0 }))}
                      style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              {/* Notes & Change Reason */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Notes</label>
                <textarea value={propsForm.notes} onChange={e => setPropsForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: `1px solid ${t.cardBorder}`, borderRadius: 8, backgroundColor: t.bg, color: t.text, boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: '#d97706', marginBottom: 4 }}>Reason for Change (optional)</label>
                <input value={propsChangeReason} onChange={e => setPropsChangeReason(e.target.value)} placeholder="e.g., Change order #3, Owner request..."
                  style={{ width: '100%', padding: '8px 12px', fontSize: 14.5, border: '1px solid #fbbf24', borderRadius: 8, backgroundColor: '#fffbeb', color: t.text, boxSizing: 'border-box' }} />
              </div>

            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.cardBorder}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setPropsModal(null)} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 14.5, fontWeight: 600, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.text, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handlePropsSave} disabled={propsSaving}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 14.5, fontWeight: 600, border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: propsSaving ? 'wait' : 'pointer', opacity: propsSaving ? 0.6 : 1 }}>
                {propsSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOVTab;
